import axios from "axios";
import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa"; // Icons for Accept and Decline
import { useNavigate } from "react-router-dom";

const TrainerDashboard = () => {
  const [selectedPackage, setSelectedPackage] = useState("All");
  const [sortOption, setSortOption] = useState("Latest Joined Date");
  const [trainees, setTrainees] = useState([]);
  const [attendanceMode, setAttendanceMode] = useState(false);
  const [showPersonalTraining, setShowPersonalTraining] = useState(true); // State to toggle views
  const navigate = useNavigate();

  const token = localStorage.getItem("gymtoken");

  useEffect(() => {
    const fetchTrainerClients = async () => {
      try {
        const response = await axios.get('https://klimkaara-backend-pgnq.onrender.com/trainers/getTrainerClients/' + token);
        if(response.status === 200){
          setTrainees(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    }

    fetchTrainerClients();
  }, [token]);

  const handlePackageChange = (event) => {
    setSelectedPackage(event.target.value);
  }

  const handleSortChange = (event) => {
    setSortOption(event.target.value);
  }

  const handleAttendanceClick = () => {
    setAttendanceMode(!attendanceMode);
  };

  const handleAttendance = (name, status) => {
    console.log(`Attendance for ${name}: ${status}`);
    // Logic for accepting or declining attendance can be added here
  };

  const filteredTrainees = trainees.filter((trainee) => {
    if (selectedPackage === "All") return true;
    return trainee.plan.toString() === selectedPackage;
  }).sort((a, b) => {
    if (sortOption === "Latest Joined Date") return b.start - a.start;
    if (sortOption === "Oldest Joined Date") return a.start - b.start;
  });

  return (
    <div className="min-h-screen bg-gray-900 text-white py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-8">
          Trainer Dashboard
        </h2>

        {/* Toggle Buttons */}
        <div className="flex justify-center mb-4">
          <button
            onClick={() => {
              setShowPersonalTraining(true);
              setAttendanceMode(false);
            }}
            className={`w-1/2 p-2 ${
              showPersonalTraining ? "bg-yellow-400" : "bg-gray-800"
            } rounded-l-lg`}
          >
            Personal Training List
          </button>
          <button
            onClick={() => {
              setShowPersonalTraining(false);
              setAttendanceMode(true);
            }}
            className={`w-1/2 p-2 ${
              !showPersonalTraining ? "bg-yellow-400" : "bg-gray-800"
            } rounded-r-lg`}
          >
            Accept Attendance
          </button>
        </div>

        {/* Content Area */}
        {showPersonalTraining ? (
          <div className="w-full py-4 grid grid-cols-1 gap-4">
            <h2 className="text-2xl font-bold text-center mb-4">
              Personal Training List
            </h2>

            {/* Package Selection Dropdown */}
            <div className="mb-4 flex justify-between">
              <select
                className="md:w-1/4 p-2 rounded-lg bg-gray-800 text-white"
                value={selectedPackage}
                onChange={handlePackageChange}
              >
                <option value="All">All</option>
                <option value="1">1 Month</option>
                <option value="3">3 Month</option>
                <option value="6">6 Month</option>
                <option value="12">1 Year</option>
              </select>

              <select
                className="w-1/2 md:w-1/4 p-2 rounded-lg bg-gray-800 text-white"
                value={sortOption}
                onChange={handleSortChange}
              >
                <option value="Latest Joined Date">Latest Joined Date</option>
                <option value="Oldest Joined Date">Oldest Joined Date</option>
              </select>
            </div>

            {/* Trainee Information as Cards */}
            {filteredTrainees.map((trainee, index) => (
              <button
                key={index}
                className="bg-gray-800 rounded-lg p-4 shadow-lg flex justify-between text-left hover:bg-gray-600"
              >
                <div>
                  <h3 className="text-xl font-semibold">{trainee.name}</h3>
                  <p className="text-gray-400 md:block hidden">
                    Package: {trainee.plan}
                  </p>
                  <p className="text-gray-400 md:block hidden">
                    Initial Weight: {trainee.weight} kg
                  </p>
                  <p className="text-gray-400 md:block hidden">
                    Current Weight: {trainee.currentWeight} kg
                  </p>
                  <p className="text-gray-400 md:block hidden">
                    Joined Date: {trainee.start ? 
                      `${trainee.start.split("T")[0].split("-")[2]}-${trainee.start.split("T")[0].split("-")[1]}-${trainee.start.split("T")[0].split("-")[0]}` 
                      : "N/A"}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="w-full py-4">
            <h2 className="text-2xl font-bold text-center mb-4">
              Attendance Management
            </h2>

            {attendanceMode && (
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg transition transform ">
                <h3 className="text-xl mb-2 font-semibold">Attendance List</h3>
                {filteredTrainees.map((trainee, index) => (
                  <div
                    key={index}
                    className="bg-gray-700 p-3 rounded-lg flex justify-between items-center mb-2 transition duration-300 ease-in-out hover:bg-gray-600"
                  >
                    <span className="text-lg font-medium">{trainee.name}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          handleAttendance(trainee.name, "Accepted")
                        }
                        className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-full hover:shadow-lg transition mr-2 transform hover:scale-110"
                        title="Accept Attendance"
                      >
                        <FaCheckCircle size={20} />
                      </button>
                      <button
                        onClick={() =>
                          handleAttendance(trainee.name, "Declined")
                        }
                        className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-400 to-red-600 text-white rounded-full hover:shadow-lg transition transform hover:scale-110"
                        title="Decline Attendance"
                      >
                        <FaTimesCircle size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* BMI Calculator Card */}
        <div
          className="bg-gray-800 p-6 rounded-lg shadow-lg w-full md:w-1/2 lg:w-1/3 hover:bg-gray-600 transition cursor-pointer"
          onClick={() => navigate("/customerbmicalculator")}
        >
          <h3 className="text-2xl font-bold mb-4">BMI Calculator</h3>
          <p className="mb-6 text-gray-400">
            Check your BMI based on your height and weight.
          </p>
          <button className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full font-semibold hover:bg-yellow-500 transition">
            Calculate BMI
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrainerDashboard;

